import React, { useCallback, useEffect, useMemo, useState } from "react";
import { fetchAPI, setAuthToken, setUnauthorizedHandler } from "./api/client";
import Sidebar from "./components/Sidebar";
import LoginScreen from "./components/LoginScreen";
import ResumenTab from "./components/tabs/ResumenTab";
import CatalogoTab from "./components/tabs/CatalogoTab";
import LibreriasTab from "./components/tabs/LibreriasTab";
import OperacionesTab from "./components/tabs/OperacionesTab";
import { parseDateFlexible, toIsoDate, toLatamDate } from "./utils/date";
import { buildLibroStockSummaries, buildStockMap, calcularImporteVenta, getStockFromMap, normalizeDescuento } from "./utils/inventory";
import { APP_VERSION } from "./version";

const todayIso = new Date().toISOString().split("T")[0];

// Producción (build subido al hosting) exige login. Cualquier otro valor (desarrollo local) entra directo.
const IS_PROD = (process.env.REACT_APP_ENV || process.env.NODE_ENV) === "production";

const LIBRO_FORM_VACIO = { id: "", titulo: "", autor: "", isbn: "", anioPublicacion: "", fechaLanzamiento: "" };
const LIBRERIA_FORM_VACIO = { id: "", nombre: "", direccion: "", contacto: "", descuento: "30" };
const operacionFormVacio = (tipo) => ({ id: "", libroId: "", libreriaId: "", cantidad: "", tipo, fecha: todayIso, precioUnitario: "", cortesia: false });

export default function App() {
  const [activeTab, setActiveTab] = useState("resumen");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);

  const [isAuthenticated, setIsAuthenticated] = useState(!IS_PROD || Boolean(sessionStorage.getItem("eh_token")));
  const [loginCredentials, setLoginCredentials] = useState({ usuario: "", clave: "" });
  const [loginError, setLoginError] = useState("");

  const [libros, setLibros] = useState([]);
  const [librerias, setLibrerias] = useState([]);
  const [operaciones, setOperaciones] = useState([]);
  const [ventas, setVentas] = useState([]);

  const [libroForm, setLibroForm] = useState(LIBRO_FORM_VACIO);
  const [libreriaForm, setLibreriaForm] = useState(LIBRERIA_FORM_VACIO);
  const [operacionTab, setOperacionTab] = useState("envio");
  const [operacionForm, setOperacionForm] = useState(operacionFormVacio("envio"));

  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const clearSession = useCallback(() => {
    setAuthToken("");
    sessionStorage.removeItem("eh_auth");
    setIsAuthenticated(!IS_PROD);
    setLibros([]);
    setLibrerias([]);
    setOperaciones([]);
    setVentas([]);
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      clearSession();
      setLoginError("Sesión expirada. Ingresá nuevamente.");
    });
    return () => setUnauthorizedHandler(null);
  }, [clearSession]);

  useEffect(() => {
    setOperacionForm(operacionFormVacio(operacionTab));
  }, [operacionTab]);

  const resetOperacionForm = useCallback(() => {
    setOperacionForm(operacionFormVacio(operacionTab));
  }, [operacionTab]);

  const cargarDatos = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [librosData, libreriasData, opsData, ventasData] = await Promise.all([
        fetchAPI("libros"),
        fetchAPI("librerias"),
        fetchAPI("operaciones"),
        fetchAPI("ventas")
      ]);

      setLibros(Array.isArray(librosData) ? librosData : []);
      setLibrerias(Array.isArray(libreriasData) ? libreriasData : []);
      setOperaciones(Array.isArray(opsData) ? opsData : []);
      setVentas(Array.isArray(ventasData) ? ventasData : []);
    } catch (err) {
      setError(err.message || "No se pudo cargar el sistema");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      cargarDatos();
    }
  }, [isAuthenticated, cargarDatos]);

  const libreriasById = useMemo(() => Object.fromEntries(librerias.map((l) => [String(l.id), l])), [librerias]);
  const librosById = useMemo(() => Object.fromEntries(libros.map((l) => [String(l.id), l])), [libros]);

  const stockMap = useMemo(() => buildStockMap(operaciones, ventas), [operaciones, ventas]);
  const getStock = useCallback((libroId, libreriaId) => getStockFromMap(stockMap, libroId, libreriaId), [stockMap]);

  const totalEjemplaresEnviados = useMemo(() => operaciones.filter((o) => o.tipo === "envio").reduce((acc, curr) => acc + (parseInt(curr.cantidad, 10) || 0), 0), [operaciones]);
  const totalEjemplaresVendidos = useMemo(() => ventas.reduce((acc, curr) => acc + (parseInt(curr.cantidad, 10) || 0), 0), [ventas]);
  const totalEjemplaresDevueltos = useMemo(() => operaciones.filter((o) => o.tipo === "devolucion").reduce((acc, curr) => acc + (parseInt(curr.cantidad, 10) || 0), 0), [operaciones]);
  const stockActualEnLibrerias = totalEjemplaresEnviados - totalEjemplaresVendidos - totalEjemplaresDevueltos;

  const dineroCobrado = useMemo(() => ventas.filter((v) => v.estado === "Cobrada").reduce((acc, curr) => acc + (Number(curr.importe) || 0), 0), [ventas]);
  const dineroPendiente = useMemo(() => ventas.filter((v) => v.estado !== "Cobrada").reduce((acc, curr) => acc + (Number(curr.importe) || 0), 0), [ventas]);
  const dineroTotalFacturado = dineroCobrado + dineroPendiente;
  const totalVentasCortesia = useMemo(() => ventas.filter((v) => Boolean(Number(v.cortesia))).length, [ventas]);

  const ventasOrdenadas = useMemo(() => [...ventas].sort((a, b) => parseDateFlexible(b.fecha) - parseDateFlexible(a.fecha)), [ventas]);

  const inventarioRows = useMemo(() => {
    const rows = [];
    librerias.forEach((libreria) => {
      libros.forEach((libro) => {
        const stock = getStock(libro.id, libreria.id);
        if (stock > 0) {
          rows.push({ key: `${libreria.id}-${libro.id}`, libreriaNombre: libreria.nombre, libroTitulo: libro.titulo, stock });
        }
      });
    });
    return rows;
  }, [librerias, libros, getStock]);

  const libroStockSummaries = useMemo(
    () => buildLibroStockSummaries(libros, librerias, ventas, stockMap),
    [libros, librerias, ventas, stockMap]
  );

  const historialOperaciones = useMemo(() => {
    if (operacionTab === "venta") {
      return ventasOrdenadas.map((v) => ({
        id: v.id,
        tipo: "venta",
        libroId: v.libroId,
        libreriaId: v.libreriaId,
        fecha: v.fecha,
        cantidad: v.cantidad,
        precioUnitario: v.precioUnitario,
        importe: v.importe,
        cortesia: Boolean(Number(v.cortesia)),
        estado: v.estado,
        libroTitulo: librosById[String(v.libroId)]?.titulo,
        libreriaNombre: libreriasById[String(v.libreriaId)]?.nombre
      }));
    }

    return operaciones
      .filter((o) => o.tipo === operacionTab)
      .sort((a, b) => parseDateFlexible(b.fecha) - parseDateFlexible(a.fecha))
      .map((o) => ({
        ...o,
        libroTitulo: librosById[String(o.libroId)]?.titulo,
        libreriaNombre: libreriasById[String(o.libreriaId)]?.nombre
      }));
  }, [operacionTab, ventasOrdenadas, operaciones, librosById, libreriasById]);

  const handleApiAction = async (fn, successMessage) => {
    setError("");
    try {
      await fn();
      await cargarDatos();
      if (successMessage) {
        showToast(successMessage, "success");
      }
    } catch (err) {
      const msg = err.message || "Operación fallida";
      setError(msg);
      showToast(msg, "error");
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const response = await fetchAPI("login", "POST", loginCredentials);
      const ok = Boolean(response?.authenticated || response?.success || response?.ok);
      if (!ok || !response?.token) {
        throw new Error(response?.message || "Credenciales inválidas");
      }
      setAuthToken(response.token);
      sessionStorage.setItem("eh_auth", "1");
      setIsAuthenticated(true);
      setLoginCredentials({ usuario: "", clave: "" });
    } catch (err) {
      setLoginError(err.message || "No se pudo iniciar sesión");
    }
  };

  const handleLogout = async () => {
    try {
      await fetchAPI("logout", "POST", {});
    } catch (err) {
    }
    clearSession();
  };

  const guardarLibro = async (e) => {
    e.preventDefault();
    const anioPublicacion = parseInt(libroForm.anioPublicacion, 10);
    if (!anioPublicacion) return showToast("Indicá el año de publicación", "error");

    const data = {
      id: libroForm.id,
      titulo: libroForm.titulo,
      autor: libroForm.autor,
      isbn: libroForm.isbn,
      anioPublicacion,
      fechaLanzamiento: libroForm.fechaLanzamiento || "",
      stockTotal: parseInt(libroForm.stockTotal, 10) || 0
    };
    await handleApiAction(async () => {
      await fetchAPI("libros", libroForm.id ? "PUT" : "POST", data);
      setLibroForm(LIBRO_FORM_VACIO);
    }, "Libro guardado");
  };

  const eliminarLibro = async (id) => {
    if (!window.confirm("¿Eliminar este libro?")) return;
    await handleApiAction(() => fetchAPI("libros", "DELETE", null, id), "Libro eliminado");
  };

  const actualizarStockTotal = async (libro, nuevoStockTotal) => {
    const stockTotal = parseInt(nuevoStockTotal, 10);
    if (Number.isNaN(stockTotal) || stockTotal < 0) {
      return showToast("El stock total debe ser un número válido", "error");
    }
    const data = {
      id: libro.id,
      titulo: libro.titulo,
      autor: libro.autor,
      isbn: libro.isbn,
      anioPublicacion: parseInt(libro.anioPublicacion, 10) || 0,
      fechaLanzamiento: libro.fechaLanzamiento || "",
      stockTotal
    };
    await handleApiAction(() => fetchAPI("libros", "PUT", data), "Stock actualizado");
  };

  const guardarLibreria = async (e) => {
    e.preventDefault();
    const data = { ...libreriaForm, descuento: normalizeDescuento(libreriaForm.descuento) };
    await handleApiAction(async () => {
      await fetchAPI("librerias", libreriaForm.id ? "PUT" : "POST", data);
      setLibreriaForm(LIBRERIA_FORM_VACIO);
    }, "Librería guardada");
  };

  const eliminarLibreria = async (id) => {
    if (!window.confirm("¿Eliminar esta librería?")) return;
    await handleApiAction(() => fetchAPI("librerias", "DELETE", null, id), "Librería eliminada");
  };

  const registrarOperacion = async (e) => {
    e.preventDefault();
    const cantidad = parseInt(operacionForm.cantidad, 10);
    if (!cantidad || cantidad <= 0) return showToast("La cantidad debe ser mayor a 0", "error");
    if (!operacionForm.fecha) return showToast("Debes indicar una fecha", "error");

    const libro = librosById[String(operacionForm.libroId)];
    const libreria = libreriasById[String(operacionForm.libreriaId)];
    if (!libro || !libreria) return showToast("Debes seleccionar libro y librería válidos", "error");

    const stockActual = getStock(libro.id, libreria.id);
    if ((operacionTab === "venta" || operacionTab === "devolucion") && cantidad > stockActual) {
      return showToast(`Stock insuficiente. Stock actual: ${stockActual}`, "error");
    }

    const precioUnitario = Number(operacionForm.precioUnitario);
    if (operacionTab === "venta" && (!precioUnitario || precioUnitario <= 0)) {
      return showToast("Indicá el precio de venta", "error");
    }

    const fechaLatam = toLatamDate(operacionForm.fecha);

    await handleApiAction(async () => {
      if (operacionTab === "venta") {
        const importe = calcularImporteVenta(precioUnitario, libreria.descuento, cantidad);
        const cortesia = operacionForm.cortesia ? 1 : 0;
        if (operacionForm.id) {
          await fetchAPI("ventas", "PUT", { libroId: libro.id, libreriaId: libreria.id, cantidad, precioUnitario, importe, cortesia, fecha: fechaLatam }, operacionForm.id);
        } else {
          await fetchAPI("ventas", "POST", { libroId: libro.id, libreriaId: libreria.id, cantidad, precioUnitario, importe, cortesia, fecha: fechaLatam });
        }
      } else if (operacionForm.id) {
        await fetchAPI("operaciones", "PUT", { id: operacionForm.id, libroId: libro.id, libreriaId: libreria.id, cantidad, tipo: operacionTab, fecha: fechaLatam });
      } else {
        await fetchAPI("operaciones", "POST", { libroId: libro.id, libreriaId: libreria.id, cantidad, tipo: operacionTab, fecha: fechaLatam });
      }
      resetOperacionForm();
    }, operacionTab === "venta" ? (operacionForm.id ? "Venta actualizada" : "Venta registrada") : operacionForm.id ? "Operación actualizada" : "Operación registrada");
  };

  const editarOperacion = (op) => {
    setOperacionForm({
      id: op.id,
      libroId: String(op.libroId),
      libreriaId: String(op.libreriaId),
      cantidad: String(op.cantidad),
      tipo: op.tipo,
      fecha: toIsoDate(op.fecha),
      precioUnitario: op.precioUnitario !== undefined && op.precioUnitario !== null ? String(op.precioUnitario) : "",
      cortesia: Boolean(Number(op.cortesia))
    });
    setOperacionTab(op.tipo);
    window.scrollTo(0, 0);
  };

  const eliminarOperacion = async (id) => {
    if (!window.confirm("¿Eliminar esta operación?")) return;
    await handleApiAction(async () => {
      if (operacionTab === "venta") {
        await fetchAPI("ventas", "DELETE", null, id);
      } else {
        await fetchAPI("operaciones", "DELETE", null, id);
      }
      if (String(operacionForm.id) === String(id)) {
        resetOperacionForm();
      }
    }, operacionTab === "venta" ? "Venta eliminada" : "Operación eliminada");
  };

  const cambiarEstadoVenta = async (ventaId, nuevoEstado) => {
    await handleApiAction(() => fetchAPI("ventas", "PUT", { estado: nuevoEstado }, ventaId), "Estado de venta actualizado");
  };

  if (!isAuthenticated) {
    return <LoginScreen credentials={loginCredentials} setCredentials={setLoginCredentials} onSubmit={handleLogin} error={loginError} />;
  }

  if (loading) return <div className="flex h-screen items-center justify-center bg-gray-50"><div className="text-xl text-indigo-600 font-medium animate-pulse">Cargando sistema desde el servidor...</div></div>;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row font-sans text-gray-800">
      <span className="fixed bottom-2 right-2 z-40 select-none rounded-md bg-white/80 px-2 py-1 text-xs text-gray-400 shadow-sm">
        v{APP_VERSION}
      </span>
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm text-white ${toast.type === "error" ? "bg-red-600" : "bg-emerald-600"}`}>
          {toast.message}
        </div>
      )}
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 p-6 md:p-10 overflow-y-auto">
        {IS_PROD && <div className="flex justify-end mb-4"><button onClick={handleLogout} className="px-3 py-2 text-sm bg-gray-200 hover:bg-gray-300 rounded-lg">Cerrar sesión</button></div>}
        {error && <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}

        {activeTab === "resumen" && (
          <ResumenTab
            stockActualEnLibrerias={stockActualEnLibrerias}
            totalEjemplaresVendidos={totalEjemplaresVendidos}
            totalVentasCortesia={totalVentasCortesia}
            dineroPendiente={dineroPendiente}
            dineroCobrado={dineroCobrado}
            dineroTotalFacturado={dineroTotalFacturado}
            inventarioRows={inventarioRows}
            ventasOrdenadas={ventasOrdenadas}
            libreriasById={libreriasById}
            onCambiarEstadoVenta={cambiarEstadoVenta}
          />
        )}

        {activeTab === "catalogo" && (
          <CatalogoTab
            libroForm={libroForm}
            setLibroForm={setLibroForm}
            libros={libros}
            libroStockSummaries={libroStockSummaries}
            onGuardarLibro={guardarLibro}
            onEditarLibro={(libro) => {
              setLibroForm({
                id: libro.id,
                titulo: libro.titulo || "",
                autor: libro.autor || "",
                isbn: libro.isbn || "",
                anioPublicacion: libro.anioPublicacion ? String(libro.anioPublicacion) : "",
                fechaLanzamiento: libro.fechaLanzamiento || "",
                stockTotal: libro.stockTotal ?? 0
              });
              window.scrollTo(0, 0);
            }}
            onEliminarLibro={eliminarLibro}
            onActualizarStockTotal={actualizarStockTotal}
          />
        )}

        {activeTab === "librerias" && (
          <LibreriasTab
            libreriaForm={libreriaForm}
            setLibreriaForm={setLibreriaForm}
            librerias={librerias}
            onGuardarLibreria={guardarLibreria}
            onEditarLibreria={(libreria) => {
              setLibreriaForm(libreria);
              window.scrollTo(0, 0);
            }}
            onEliminarLibreria={eliminarLibreria}
          />
        )}

        {activeTab === "operaciones" && (
          <OperacionesTab
            operacionForm={operacionForm}
            setOperacionForm={setOperacionForm}
            operacionTab={operacionTab}
            setOperacionTab={setOperacionTab}
            librerias={librerias}
            libros={libros}
            getStock={getStock}
            historial={historialOperaciones}
            onRegistrarOperacion={registrarOperacion}
            onEditarOperacion={editarOperacion}
            onEliminarOperacion={eliminarOperacion}
            onCancelarEdicion={resetOperacionForm}
          />
        )}
      </main>
    </div>
  );
}
